import { createContext, useContext, useState, type ReactNode } from "react"
import { getTypeForOffersFunnel } from "@apis/accounts/utils"
import { useAccount } from "@apis/accounts/useAccounts"
import type {
  IAcceptWinbackOfferResponse,
  IWinbackOffer,
} from "@apis/winbacks/types"
import type {
  IAcceptRetentionOfferResponse,
  IRetentionOffer,
} from "@apis/retention/types"

type Step =
  | "initial"
  | "chooseOffer"
  | "offerDetail"
  | "verification"
  | "cancelMember"
  | "confirmation"

export type TOfferType = "Winback" | "Retention" | undefined

type AccountOfferFunnelContextType = {
  offerType: TOfferType
  step: Step
  setStep: (step: Step) => void
  selectedOffer: IWinbackOffer | IRetentionOffer | null
  setSelectedOffer: (offer: IWinbackOffer | IRetentionOffer | null) => void
  account_id: string
  tpvType: string | null
  setTpvType: (tpv: string) => void
  prospect_guid: string | undefined
  confirmedOrder:
    | IAcceptWinbackOfferResponse
    | IAcceptRetentionOfferResponse
    | null
  setConfirmedOrder: (
    order: IAcceptWinbackOfferResponse | IAcceptRetentionOfferResponse | null,
  ) => void
}

const AccountOfferFunnelContext = createContext<
  AccountOfferFunnelContextType | undefined
>(undefined)

export const AccountOfferFunnelProvider = ({
  children,
  account_id,
}: {
  children: ReactNode
  account_id: string
}) => {
  const getAccountQuery = useAccount(account_id)

  const offerType =
    getAccountQuery.data && getTypeForOffersFunnel(getAccountQuery.data)

  const [step, setStep] = useState<Step>("initial")
  const [selectedOffer, setSelectedOffer] = useState<
    IWinbackOffer | IRetentionOffer | null
  >(null)
  const [tpvType, setTpvType] = useState<string | null>(null)
  const [confirmedOrder, setConfirmedOrder] = useState<
    IAcceptWinbackOfferResponse | IAcceptRetentionOfferResponse | null
  >(null)

  const prospect_guid = getAccountQuery.data?.prospect_guid

  return (
    <AccountOfferFunnelContext.Provider
      value={{
        offerType,
        step,
        setStep,
        selectedOffer,
        setSelectedOffer,
        account_id,
        tpvType,
        setTpvType,
        prospect_guid,
        confirmedOrder,
        setConfirmedOrder,
      }}
    >
      {children}
    </AccountOfferFunnelContext.Provider>
  )
}

export const useAccountOfferFunnel = () => {
  const context = useContext(AccountOfferFunnelContext)
  if (!context) {
    throw new Error(
      "useAccountOfferFunnel must be used inside AccountOfferFunnelProvider",
    )
  }
  return context
}
